import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import Avatar from "react-avatar";
import Swal from "sweetalert2";
import { obtenerPerfil, actualizarPerfil } from "../redux/profileSlice";
import {
  fetchRegiones,
  fetchComunasByRegion,
} from "../redux/regionComunaSlice";
import "../styles/ProfilePage.css";

// === helpers ===
const REGEX_NOMBRE_MODELO = /^[a-zA-Z]+$/; // SIN tildes, SIN espacios (como tu modelo)
const isValidNombre = (s) => REGEX_NOMBRE_MODELO.test((s || "").trim());

const ProfilePage = () => {
  const dispatch = useDispatch();
  const { usuario } = useSelector((state) => state.profile);
  const { regiones = [], comunas = [] } = useSelector(
    (state) => state.regionComuna
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const setErr = (name, msg) =>
    setFieldErrors((prev) => ({ ...prev, [name]: msg || "" }));

  const [formData, setFormData] = useState({
    nombre: "",
    primer_apellido: "",
    segundo_apellido: "",
    telefono: "",
    direccion: "",
    email_personal: "",
    contacto_emergencia: "",
    telefono_emergencia: "",
    region_id: "",
    comuna_id: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [defaultValuesDetected, setDefaultValuesDetected] = useState({
    apellidos: false,
    telefono: false,
    direccion: false,
    contactoEmergencia: false,
  });

  // Cargar regiones y perfil al montar el componente
  useEffect(() => {
    dispatch(obtenerPerfil());
    dispatch(fetchRegiones());
  }, [dispatch]);

  // Cargar datos del usuario y comunas si ya tiene región seleccionada
  useEffect(() => {
    if (usuario) {
      const newFormData = {
        nombre: usuario.nombre || "",
        primer_apellido: usuario.primer_apellido || "",
        segundo_apellido: usuario.segundo_apellido || "",
        telefono: usuario.telefono || "",
        direccion: usuario.direccion || "",
        email_personal: usuario.email_personal || "",
        contacto_emergencia: usuario.contacto_emergencia || "",
        telefono_emergencia: usuario.telefono_emergencia || "",
        region_id: usuario.usuario_comuna?.region_id || "",
        comuna_id: usuario.comuna_id || "",
      };

      // Resetear región/comuna si son "NO ESPECIFICADA"
      if (
        usuario.usuario_comuna?.region_nombre
          ?.toUpperCase()
          .includes("NO ESPECIFICADA")
      ) {
        newFormData.region_id = "";
        newFormData.comuna_id = "";
      }

      // Detectar valores por defecto
      const defaults = {
        apellidos:
          ["Apellido", "apellido"].includes(newFormData.primer_apellido) ||
          ["Apellido", "apellido"].includes(newFormData.segundo_apellido),
        telefono: newFormData.telefono === "+56912345678",
        direccion: [
          "Dirección no especificada",
          "direccion no especificada",
        ].includes(newFormData.direccion),
        contactoEmergencia: [
          "Contacto Emergencia",
          "contacto emergencia",
        ].includes(newFormData.contacto_emergencia),
      };

      setDefaultValuesDetected(defaults);
      setFormData(newFormData);

      if (
        newFormData.region_id &&
        !usuario.usuario_comuna?.region_nombre
          ?.toUpperCase()
          .includes("NO ESPECIFICADA")
      ) {
        dispatch(fetchComunasByRegion(newFormData.region_id));
      }
    }
  }, [usuario, dispatch]);

  const handleRegionChange = (e) => {
    const regionId = e.target.value;
    setFormData({
      ...formData,
      region_id: regionId,
      comuna_id: "",
    });

    if (regionId) {
      dispatch(fetchComunasByRegion(regionId));
    }
  };

  // Validación mejorada según authController
  const validateField = (name, value) => {
    let error = "";
    const trimmedValue = value ? value.toString().trim() : "";

    // Campos opcionales
    const optionalFields = [
      "email_personal",
      "contacto_emergencia",
      "telefono_emergencia",
      "segundo_apellido",
    ];
    if (optionalFields.includes(name) && trimmedValue === "") {
      return "";
    }

    switch (name) {
      case "nombre":
      if (!trimmedValue) {
         error = "El nombre es obligatorio";
       } else if (!/^[A-Za-z]+$/.test(trimmedValue)) {
         // Alinear con el modelo: sin tildes ni espacios
         error = "Usa solo letras A–Z, sin tildes ni espacios (restricción del sistema)";
       } else if (trimmedValue.length < 2) {
         error = "Debe tener al menos 2 caracteres";
       }
       break;
      case "primer_apellido":
      case "segundo_apellido":
        if (!trimmedValue && name !== "segundo_apellido") {
          error = "Este campo es requerido";
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(trimmedValue)) {
          error = "Solo puede contener letras y espacios";
        } else if (trimmedValue.length < 2) {
          error = "Debe tener al menos 2 caracteres";
        } else if (["Apellido", "apellido"].includes(trimmedValue)) {
          error = "Debes ingresar un apellido válido";
        }
        break;

      case "telefono":
        if (!trimmedValue) {
          error = "Este campo es requerido";
        } else if (!/^\+56\d{8,9}$/.test(trimmedValue)) {
          error = "Formato: +56912345678 o +5621234567";
        } else if (trimmedValue === "+56912345678") {
          error = "Debes cambiar el teléfono por defecto";
        }
        break;

      case "telefono_emergencia":
        if (trimmedValue && !/^\+56\d{8,9}$/.test(trimmedValue)) {
          error = "Formato: +56912345678 o +5621234567";
        }
        break;

      case "email_personal":
        if (trimmedValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedValue)) {
          error = "Email inválido";
        }
        break;

      case "region_id":
        if (!trimmedValue) {
          error = "Seleccione una región";
        }
        break;

      case "comuna_id":
        if (formData.region_id && !trimmedValue) {
          error = "Seleccione una comuna";
        }
        break;

      case "direccion":
        if (!trimmedValue) {
          error = "Este campo es requerido";
        } else if (
          ["Dirección no especificada", "direccion no especificada"].includes(
            trimmedValue
          )
        ) {
          error = "Debes ingresar una dirección válida";
        } else if (trimmedValue.length < 5) {
          error = "La dirección debe tener al menos 5 caracteres";
        } else if (trimmedValue.length > 255) {
          error = "La dirección no puede exceder los 255 caracteres";
        }
        break;

      case "contacto_emergencia":
        if (trimmedValue && trimmedValue.length < 2) {
          error = "El nombre debe tener al menos 2 caracteres";
        } else if (
          ["Contacto Emergencia", "contacto emergencia"].includes(trimmedValue)
        ) {
          error = "Debes ingresar un nombre válido";
        }
        break;

      default:
        if (!trimmedValue && name !== "segundo_apellido") {
          error = "Este campo es requerido";
        }
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validación en tiempo real solo para campos tocados
    if (touchedFields[name]) {
      const error = validateField(name, value);
      setFormErrors({ ...formErrors, [name]: error });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields({ ...touchedFields, [name]: true });

    // Validación al perder foco
    const error = validateField(name, value);
    setFormErrors({ ...formErrors, [name]: error });
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validar todos los campos requeridos
    Object.keys(formData).forEach((key) => {
      if (key !== "comuna_id" || formData.region_id) {
        const error = validateField(key, formData[key]);
        if (error) {
          errors[key] = error;
          isValid = false;
        }
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!formData.nombre?.trim()){
      errs.nombre = 'El nombre es obligatorio.';
    }else if(!isValidNombre(formData.nombre)){
    errs.nombre = 'Usa solo letras A–Z, sin tildes ni espacios.';
    }
    if(Object.keys(errs).length){
      setFieldErrors(errs);
      Swal.fire({ icon: 'error', title: 'Corrige el campo Nombre', text: errs.nombre });
      return;
    }
    // Marcar todos los campos como tocados para mostrar errores
    const allFieldsTouched = Object.keys(formData).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouchedFields(allFieldsTouched);

    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Por favor corrige los errores en el formulario",
        timer: 2000,
      });
      return;
    }

    setIsUpdating(true);

    try {
      const { region_id, ...dataToSend } = formData;
      const resultAction = await dispatch(actualizarPerfil(dataToSend));

      if (actualizarPerfil.fulfilled.match(resultAction)) {
        await dispatch(obtenerPerfil());
        Swal.fire({
          icon: "success",
          title: "¡Perfil actualizado!",
          text: "Tus datos se han guardado correctamente.",
          timer: 2000,
        });
      } else {
        throw new Error(resultAction.payload || "Error al actualizar perfil");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message,
        timer: 2000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Función para mostrar si un campo es requerido
  const isRequiredField = (fieldName) => {
    const optionalFields = [
      "email_personal",
      "contacto_emergencia",
      "telefono_emergencia",
      "segundo_apellido",
    ];
    return !optionalFields.includes(fieldName);
  };

  return (
    <Container className="profile-container">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="profile-card">
            <Card.Body>
              <div className="profile-header text-center mb-4">
                <Avatar
                  name={`${formData.nombre} ${formData.primer_apellido}`}
                  size="120"
                  round={true}
                  className="profile-avatar"
                  color="#d32f2f"
                />
                <h3 className="profile-name">
                  {formData.nombre} {formData.primer_apellido}
                </h3>
              </div>

              {/* Alertas para valores por defecto */}
              {defaultValuesDetected.apellidos && (
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Tienes apellidos por defecto. Por favor actualízalos con tus
                  datos reales.
                </Alert>
              )}

              {defaultValuesDetected.telefono && (
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-phone-vibrate-fill me-2"></i>
                  Tienes un número de teléfono por defecto. Por favor
                  actualízalo con tu número real.
                </Alert>
              )}

              {defaultValuesDetected.direccion && (
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-house-exclamation-fill me-2"></i>
                  Tienes una dirección por defecto. Por favor actualízala con tu
                  dirección real.
                </Alert>
              )}

              {defaultValuesDetected.contactoEmergencia && (
                <Alert variant="warning" className="mb-3">
                  <i className="bi bi-person-exclamation me-2"></i>
                  Tienes un contacto de emergencia por defecto. Por favor
                  actualízalo con un nombre válido.
                </Alert>
              )}

              <Form onSubmit={handleFormSubmit} noValidate>
                {/* Sección Datos Personales */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <i className="bi bi-person-lines-fill me-2"></i>
                      Datos personales
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group controlId="perfilNombre" className="mb-3">
                          <Form.Label>Nombre</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.nombre}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFormData((p) => ({ ...p, nombre: v }));
                              if (!v.trim())
                                return setErr(
                                  "nombre",
                                  "El nombre es obligatorio."
                                );
                              if (!isValidNombre(v)) {
                                return setErr(
                                  "nombre",
                                  "El sistema no permite tildes ni espacios en Nombre. Usa solo letras A–Z, sin espacios."
                                );
                              }
                              setErr("nombre", "");
                            }}
                            onBlur={(e) => {
                              const v = e.target.value;
                              if (!v.trim())
                                return setErr(
                                  "nombre",
                                  "El nombre es obligatorio."
                                );
                              if (!isValidNombre(v)) {
                                return setErr(
                                  "nombre",
                                  "El sistema no admite tildes ni espacios en Nombre. Usa solo letras A–Z, sin espacios."
                                );
                              }
                              setErr("nombre", "");
                            }}
                            isInvalid={!!fieldErrors.nombre}
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {fieldErrors.nombre}
                          </Form.Control.Feedback>
                          <Form.Text muted>
                            (Restricción actual del sistema:{" "}
                            <strong>sin tildes ni espacios</strong>. Los
                            compuestos puedes colocarlos en Apellidos.)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Primer apellido{" "}
                            {isRequiredField("primer_apellido") && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Form.Control
                            name="primer_apellido"
                            value={formData.primer_apellido}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touchedFields.primer_apellido &&
                              !!formErrors.primer_apellido
                            }
                            required={isRequiredField("primer_apellido")}
                          />
                          <Form.Control.Feedback type="invalid">
                            {formErrors.primer_apellido}
                          </Form.Control.Feedback>
                          {defaultValuesDetected.apellidos &&
                            formData.primer_apellido && (
                              <Form.Text className="text-warning">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                Debes cambiar el apellido por defecto
                              </Form.Text>
                            )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Segundo apellido</Form.Label>
                      <Form.Control
                        name="segundo_apellido"
                        value={formData.segundo_apellido}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.segundo_apellido &&
                          !!formErrors.segundo_apellido
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.segundo_apellido}
                      </Form.Control.Feedback>
                      {defaultValuesDetected.apellidos &&
                        formData.segundo_apellido && (
                          <Form.Text className="text-warning">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            Debes cambiar el apellido por defecto
                          </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Teléfono{" "}
                        {isRequiredField("telefono") && (
                          <span className="text-danger">*</span>
                        )}
                      </Form.Label>
                      <Form.Control
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.telefono && !!formErrors.telefono
                        }
                        required={isRequiredField("telefono")}
                        placeholder="+56912345678"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.telefono}
                      </Form.Control.Feedback>
                      {defaultValuesDetected.telefono && (
                        <Form.Text className="text-warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Debes cambiar el teléfono por defecto
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Sección Dirección */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <i className="bi bi-house-door-fill me-2"></i>
                      Dirección
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Región{" "}
                            {isRequiredField("region_id") && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <Form.Select
                            name="region_id"
                            value={formData.region_id}
                            onChange={handleRegionChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touchedFields.region_id && !!formErrors.region_id
                            }
                            required={isRequiredField("region_id")}
                          >
                            <option value="">Seleccione región</option>
                            {regiones
                              .filter(
                                (region) =>
                                  !region.nombre
                                    .toUpperCase()
                                    .includes("NO ESPECIFICADA")
                              )
                              .map((region) => (
                                <option key={region.id} value={region.id}>
                                  {region.nombre}
                                </option>
                              ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {formErrors.region_id}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Comuna{" "}
                            {formData.region_id &&
                              isRequiredField("comuna_id") && (
                                <span className="text-danger">*</span>
                              )}
                          </Form.Label>
                          <Form.Select
                            name="comuna_id"
                            value={formData.comuna_id}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            isInvalid={
                              touchedFields.comuna_id && !!formErrors.comuna_id
                            }
                            disabled={!formData.region_id}
                            required={
                              formData.region_id && isRequiredField("comuna_id")
                            }
                          >
                            <option value="">Seleccione comuna</option>
                            {comunas
                              .filter(
                                (comuna) =>
                                  !comuna.nombre
                                    .toUpperCase()
                                    .includes("NO ESPECIFICADA")
                              )
                              .map((comuna) => (
                                <option key={comuna.id} value={comuna.id}>
                                  {comuna.nombre}
                                </option>
                              ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {formErrors.comuna_id}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Dirección{" "}
                        {isRequiredField("direccion") && (
                          <span className="text-danger">*</span>
                        )}
                      </Form.Label>
                      <Form.Control
                        name="direccion"
                        value={formData.direccion}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.direccion && !!formErrors.direccion
                        }
                        required={isRequiredField("direccion")}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.direccion}
                      </Form.Control.Feedback>
                      {defaultValuesDetected.direccion && (
                        <Form.Text className="text-warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Debes cambiar la dirección por defecto
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Sección Contacto de Emergencia */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <i className="bi bi-person-plus-fill me-2"></i>
                      Contacto de emergencia
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Nombre contacto</Form.Label>
                      <Form.Control
                        name="contacto_emergencia"
                        value={formData.contacto_emergencia}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.contacto_emergencia &&
                          !!formErrors.contacto_emergencia
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.contacto_emergencia}
                      </Form.Control.Feedback>
                      {defaultValuesDetected.contactoEmergencia && (
                        <Form.Text className="text-warning">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          Debes cambiar el contacto por defecto
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Teléfono contacto</Form.Label>
                      <Form.Control
                        name="telefono_emergencia"
                        value={formData.telefono_emergencia}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.telefono_emergencia &&
                          !!formErrors.telefono_emergencia
                        }
                        placeholder="+56912345678"
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.telefono_emergencia}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Sección Email Personal */}
                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      <i className="bi bi-envelope-fill me-2"></i>
                      Email personal
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>Email personal</Form.Label>
                      <Form.Control
                        type="email"
                        name="email_personal"
                        value={formData.email_personal}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touchedFields.email_personal &&
                          !!formErrors.email_personal
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.email_personal}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save-fill me-2"></i>
                        Guardar cambios
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
